import type { Node } from '../../../core/models/scene';
import type {
  MultiViewEntityDetail,
  MultiViewEntityDetailRow,
  MultiViewEntityRef,
} from '../../../core/multi-view';

type DetailTone = NonNullable<MultiViewEntityDetail['tone']>;

/**
 * Atlas-owned resolver for projection canvas selection details.
 *
 * The generic projection canvas only asks for entity refs and rows to render;
 * Atlas keeps workload, target, source, and confidence labels here.
 */
export class OrgAllocationProjectionDetailService {
  entityRefForNode(node: Node): MultiViewEntityRef | null {
    const gantt = recordValue(node.attrs?.atlasGantt);
    if (gantt) return entityRefFromAtlasMetadata(gantt, node, 'atlas.workload-gantt');

    const planning = recordValue(node.attrs?.atlasPlanning);
    if (planning) return entityRefFromAtlasMetadata(planning, node, 'atlas.capacity-planner');

    const diff = recordValue(node.attrs?.atlasDiff);
    if (diff) return entityRefFromAtlasMetadata(diff, node, 'atlas.load-diff');

    return null;
  }

  detailForNode(node: Node): MultiViewEntityDetail | null {
    const gantt = recordValue(node.attrs?.atlasGantt);
    if (gantt) return detailForGanttNode(node, gantt);

    const planning = recordValue(node.attrs?.atlasPlanning);
    if (planning) return detailForPlanningNode(node, planning);

    const diff = recordValue(node.attrs?.atlasDiff);
    if (diff) return detailForDiffNode(node, diff);

    return null;
  }
}

function detailForGanttNode(node: Node, metadata: Record<string, unknown>): MultiViewEntityDetail | null {
  const kind = stringValue(metadata.kind);
  if (kind === 'workload_bar') {
    const sourceState = stringValue(metadata.sourceState) ?? 'source_backed';
    const tone = toneForSourceState(sourceState);
    const rowKind = stringValue(metadata.rowKind);
    const aggregateField = stringValue(metadata.aggregateField);
    const aggregateValue = stringValue(metadata.aggregateValue);
    const memberCount = numberValue(metadata.memberCount);
    const targetLabel = stringValue(metadata.targetLabel) ??
      labelFromNode(node) ??
      stringValue(metadata.targetExternalId) ??
      'Work';
    const personLabel = stringValue(metadata.personLabel) ??
      stringValue(metadata.personExternalId) ??
      'Person';
    const rows: MultiViewEntityDetailRow[] = [
      row(
        rowKind === 'aggregate' ? 'group' : 'person',
        rowKind === 'aggregate' ? 'Group' : 'Person',
        rowKind === 'aggregate' && aggregateField && aggregateValue
          ? `${titleCase(aggregateField)}: ${aggregateValue}`
          : personLabel,
      ),
      row('target', 'Target', targetLabel),
      row('period', 'Period', stringValue(metadata.periodLabel) ?? stringValue(metadata.periodKey) ?? 'Unknown'),
      row('allocation', 'Allocation', allocationLabel(metadata)),
      row('status', 'Status', sourceStateLabel(sourceState), toneForSourceState(sourceState)),
    ];
    if (rowKind === 'aggregate' && memberCount != null) {
      appendOptionalRow(rows, 'members', 'People', String(memberCount));
    }
    appendOptionalRow(rows, 'role', 'Role', stringValue(metadata.role));
    appendOptionalRow(rows, 'confidence', 'Confidence', confidenceLabel(metadata.confidence));
    appendOptionalRow(rows, 'source', 'Source', listLabel(metadata.sourceLabels));
    appendOptionalRow(rows, 'window', 'Window', windowLabel(metadata.validFrom, metadata.validTo));
    appendOptionalRow(rows, 'records', 'Records', listLabel(metadata.relationshipRecordIds));

    return {
      entityRef: entityRefFromAtlasMetadata(metadata, node, 'atlas.workload-gantt'),
      title: targetLabel,
      subtitle: `${rowKind === 'aggregate' ? aggregateValue ?? personLabel : personLabel} - ${allocationLabel(metadata)}`,
      tone,
      badges: [
        rowKind === 'aggregate' ? 'Aggregated' : null,
        sourceStateLabel(sourceState),
        stringValue(metadata.targetKind),
      ].filter((value): value is string => !!value),
      rows,
      sections: [{
        id: 'resolution',
        title: 'Resolution',
        rows: [
          row('match', 'Mapping', sourceState === 'unresolved' ? 'Needs identity or target resolution' : 'Resolved for this projection', sourceState === 'unresolved' ? 'warning' : 'success'),
          row('mutation', 'Source facts', sourceState === 'draft' ? 'Scenario overlay only' : 'Read-only source-backed fact', sourceState === 'draft' ? 'warning' : 'muted'),
          ...(rowKind === 'aggregate'
            ? [row('drilldown', 'Drilldown', 'Select this row to filter to people in this group', 'muted' as const)]
            : []),
        ],
      }],
    };
  }

  if (kind === 'capacity_guide') {
    return {
      entityRef: entityRefFromAtlasMetadata(metadata, node, 'atlas.workload-gantt'),
      title: 'Capacity guide',
      subtitle: stringValue(metadata.periodKey) ?? undefined,
      rows: [
        row('known-load', 'Known load', formatPercent(numberValue(metadata.knownLoad) ?? 0)),
        row('meaning', 'Meaning', '100% capacity line'),
      ],
    };
  }

  if (kind === 'workload_gantt_table') {
    return {
      title: 'Workload Gantt',
      subtitle: 'Projection summary',
      rows: [
        row('people', 'Visible people', String(numberValue(metadata.peopleCount) ?? 0)),
        row('periods', 'Visible periods', String(numberValue(metadata.periodCount) ?? 0)),
        row('granularity', 'Granularity', stringValue(metadata.granularity) ?? 'month'),
      ],
    };
  }

  return null;
}

function detailForPlanningNode(node: Node, metadata: Record<string, unknown>): MultiViewEntityDetail | null {
  const kind = stringValue(metadata.kind);
  if (kind === 'person_card') {
    const total = numberValue(metadata.totalAllocation) ?? 0;
    return {
      entityRef: entityRefFromAtlasMetadata(metadata, node, 'atlas.capacity-planner'),
      title: stringValue(node.name) ?? stringValue(metadata.personExternalId) ?? 'Person',
      subtitle: 'Capacity planning row',
      tone: total > 1 ? 'danger' : 'success',
      badges: [
        stringValue(metadata.status),
        stringValue(metadata.lane),
      ].filter((value): value is string => !!value),
      rows: [
        row('load', 'Load', formatPercent(total), total > 1 ? 'danger' : 'success'),
        row('commitments', 'Commitments', String(numberValue(metadata.assignmentCount) ?? 0)),
        row('work', 'Work', stringValue(metadata.topTargets) ?? 'No named target'),
      ],
    };
  }

  if (kind === 'target_card') {
    return {
      entityRef: entityRefFromAtlasMetadata(metadata, node, 'atlas.capacity-planner'),
      title: stringValue(node.name) ?? stringValue(metadata.targetLabel) ?? 'Work target',
      subtitle: stringValue(metadata.targetKind) ?? 'target',
      rows: [
        row('load', 'Demand', formatPercent(numberValue(metadata.totalAllocation) ?? 0)),
        row('people', 'People', String(numberValue(metadata.peopleCount) ?? 0)),
        row('allocation', 'Allocation', metadata.hasUnspecifiedAllocation ? 'Includes unknown allocation' : 'Known allocation'),
      ],
    };
  }

  if (kind === 'lane' || kind === 'kpi') {
    return {
      title: labelFromNode(node) ?? stringValue(metadata.key) ?? 'Planning item',
      subtitle: kind === 'lane' ? 'Planning lane' : 'Planning metric',
      rows: [
        row('type', 'Type', kind),
        row('key', 'Key', stringValue(metadata.key) ?? 'unknown'),
      ],
    };
  }

  return null;
}

function detailForDiffNode(node: Node, metadata: Record<string, unknown>): MultiViewEntityDetail | null {
  const personLabel = stringValue(metadata.personLabel) ??
    stringValue(node.name) ??
    stringValue(metadata.personExternalId) ??
    'Person';
  const delta = numberValue(metadata.deltaAllocation);
  return {
    entityRef: entityRefFromAtlasMetadata(metadata, node, 'atlas.load-diff'),
    title: personLabel,
    subtitle: 'Load comparison',
    tone: delta != null && delta > 0 ? 'warning' : 'success',
    rows: [
      row('change', 'Change', delta == null ? 'Unknown' : signedPercent(delta), delta != null && delta > 0 ? 'warning' : 'success'),
      row('before', 'Before', formatPercent(numberValue(metadata.beforeAllocation) ?? 0)),
      row('after', 'After', formatPercent(numberValue(metadata.afterAllocation) ?? 0)),
    ],
  };
}

function entityRefFromAtlasMetadata(
  metadata: Record<string, unknown>,
  node: Node,
  sourcePaneId: string,
): MultiViewEntityRef | null {
  const aggregateField = stringValue(metadata.aggregateField);
  const aggregateValue = stringValue(metadata.aggregateValue);
  if (aggregateField && aggregateValue) {
    return {
      id: `aggregate:${aggregateField}:${aggregateValue}`,
      kind: 'aggregate',
      domain: 'atlas',
      label: `${titleCase(aggregateField)}: ${aggregateValue}`,
      sourcePaneId,
      resolved: true,
    };
  }

  const personId = stringValue(metadata.personExternalId) ?? stringValue(metadata.personId);
  if (personId) {
    return {
      id: personId,
      kind: 'person',
      domain: 'atlas',
      label: stringValue(metadata.personLabel) ?? stringValue(node.name) ?? personId,
      sourcePaneId,
      resolved: true,
    };
  }

  const targetId = stringValue(metadata.targetExternalId);
  if (targetId) {
    return {
      id: targetId,
      kind: stringValue(metadata.targetKind) ?? 'work_target',
      domain: 'atlas',
      label: stringValue(metadata.targetLabel) ?? stringValue(node.name) ?? targetId,
      sourcePaneId,
      resolved: true,
    };
  }

  return null;
}

function row(
  id: string,
  label: string,
  value: string,
  tone?: MultiViewEntityDetailRow['tone'],
): MultiViewEntityDetailRow {
  return { id, label, value, ...(tone ? { tone } : {}) };
}

function appendOptionalRow(
  rows: MultiViewEntityDetailRow[],
  id: string,
  label: string,
  value: string | null,
): void {
  if (value) rows.push(row(id, label, value));
}

function labelFromNode(node: Node): string | null {
  return stringValue(node.text) ??
    stringValue(node.name) ??
    stringValue(node.attrs?.label) ??
    stringValue(node.attrs?.title);
}

function allocationLabel(metadata: Record<string, unknown>): string {
  if (metadata.hasUnknownAllocation === true) return 'Unknown';
  const allocation = numberValue(metadata.allocation);
  return allocation == null ? 'Unknown' : formatPercent(allocation);
}

function confidenceLabel(value: unknown): string | null {
  const confidence = numberValue(value);
  return confidence == null ? null : formatPercent(confidence);
}

function windowLabel(start: unknown, end: unknown): string | null {
  const from = stringValue(start);
  const to = stringValue(end);
  if (!from && !to) return null;
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  return `Until ${to}`;
}

function listLabel(value: unknown): string | null {
  const values = Array.isArray(value)
    ? value.map(stringValue).filter((item): item is string => !!item)
    : [stringValue(value)].filter((item): item is string => !!item);
  if (values.length === 0) return null;
  if (values.length <= 2) return values.join(', ');
  return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
}

function sourceStateLabel(value: string): string {
  switch (value) {
    case 'draft':
      return 'Draft scenario';
    case 'needs_review':
      return 'Needs review';
    case 'stale':
      return 'Stale source';
    case 'retired':
      return 'Retired';
    case 'unresolved':
      return 'Unresolved';
    case 'low_confidence':
      return 'Low confidence';
    case 'source_backed':
    default:
      return 'Source-backed';
  }
}

function toneForSourceState(value: string): DetailTone {
  switch (value) {
    case 'retired':
    case 'unresolved':
      return 'danger';
    case 'draft':
    case 'needs_review':
    case 'stale':
    case 'low_confidence':
      return 'warning';
    case 'source_backed':
    default:
      return 'success';
  }
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function signedPercent(value: number): string {
  const rounded = Math.round(value * 100);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function numberValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/%$/, ''));
    if (Number.isFinite(parsed)) return parsed > 1 ? parsed / 100 : parsed;
  }
  return null;
}

function stringValue(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const raw = String(value).trim();
  return raw ? raw : null;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}
